import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Typography,
  Chip,
  Paper,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { API_URL } from "../../config";

const SettingsSubscription = ({ data, token }) => {
  const navigate = useNavigate();
  
  const subscriptionTier = {
    0: { d: "You do not have a current subscription", c: "", b: "" },
    1: {
      d: "Your current subscription is ",
      c: "Starter",
      b: "starter-subscription-border-wrap border-0 px-2 font-weight-bold badge text-white",
    },
    2: {
      d: "Your current subscription is ",
      c: "Basic", 
      b: "basic-subscription-border-wrap border-0 px-2 font-weight-bold badge text-white",
    },
    3: {
      d: "Your current subscription is ",
      c: "Plus",
      b: "plus-subscription-border-wrap border-0 px-2 font-weight-bold badge text-white",
    },
    4: {
      d: "Your current subscription is ",
      c: "Premium",
      b: "premium-subscription-border-wrap border-0 px-2 font-weight-bold badge text-white",
    },
  };

  const [currentSubscription, setCurrentSubscription] = useState(data?.app_metadata?.subscription || '0');
  const [limits, setLimits] = useState(data?.app_metadata?.limits || {});
  const [current, setCurrent] = useState(data?.app_metadata?.current || {});
  const [photoLimit, setPhotoLimit] = useState("");
  const [emailLimit, setEmailLimit] = useState("");
  const [storageGB, setStorageGB] = useState(0);
  const [storageGBLimit, setStorageGBLimit] = useState(0);
  const [subscriptionDescription, setSubscriptionDescription] = useState("Subscribe now to avail more of ShieldShot's services!");
  const [cancelSubscription, setCancelSubscription] = useState(false);
  const [reason, setReason] = useState("");
  const [redirect, setRedirect] = useState(false);

  useEffect(() => {
    if (data?.app_metadata) {
      setCurrentSubscription(data.app_metadata.subscription || '0');
      setLimits(data.app_metadata.limits || {});
      setCurrent(data.app_metadata.current || {});
    }
  }, [data]);

  useEffect(() => {
    if (limits.photos) {
      if (parseInt(limits.photos, 10) === 9999999999) {
        setPhotoLimit(" (unlimited) ");
      } else {
        setPhotoLimit(" of " + limits.photos);
      }
    }

    if (limits.emails) {
      if (parseInt(limits.emails, 10) === 9999999999) {
        setEmailLimit(" (unlimited) ");
      } else {
        setEmailLimit(" of " + limits.emails);
      }
    }

    if (current.storage && limits.storage) {
      setStorageGB((current.storage / 1073741824).toFixed(2));
      setStorageGBLimit(limits.storage / 1073741824);
    }
  }, [limits, current]);

  useEffect(() => {
    if (currentSubscription === "4") {
      setSubscriptionDescription("");
    } else if (currentSubscription === "0") {
      setSubscriptionDescription("Subscribe now to avail of ShieldShot's services!");
    } else {
      setSubscriptionDescription("Subscribe now to avail more of ShieldShot's services!");
    }
  }, [currentSubscription]);

  const handleReason = (e) => {
    setReason(e.target.value);
  };

  const submitCancel = async (e) => {
    e.preventDefault();
    console.log("cancelling...");
    
    if (!reason.trim()) {
      toast.warning("Please provide a reason for cancellation");
      return;
    }

    try {
      const body = JSON.stringify({
        reason: reason,
      });

      const response = await fetch(
        API_URL + "/users/v1/cancel",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: body,
          method: "POST",
        }
      );

      if (response.ok) {
        setRedirect(true);
      } else {
        const errorData = await response.json();
        toast.error(`Failed to cancel subscription: ${errorData.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Cancel subscription error:', err);
      toast.error("Something went wrong with cancelling your subscription: Server cannot be reached.");
    }
  };

  useEffect(() => {
    if (redirect) {
      navigate("/dashboard", {
        state: {
          alertMessage: "You have cancelled your subscription",
          alertType: "success",
        },
        replace: true,
      });
    }
  }, [redirect, navigate]);

  const getSubscriptionTierColor = (tier) => {
    switch (tier) {
      case '1':
        return { bgcolor: '#4caf50', color: 'white' }; // Green for Starter
      case '2':
        return { bgcolor: '#2196f3', color: 'white' }; // Blue for Basic
      case '3':
        return { bgcolor: '#ff9800', color: 'white' }; // Orange for Plus
      case '4':
        return { bgcolor: '#9c27b0', color: 'white' }; // Purple for Premium
      default:
        return { bgcolor: 'grey.500', color: 'white' };
    }
  };

  return (
    <>
      <Dialog
        open={cancelSubscription}
        onClose={() => {
          setCancelSubscription(false);
          setReason("");
        }}
      >
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Can you tell us why you're cancelling your subscription? You'll
            still retain your privileges until the end of your subscription
            period.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="reason"
            label="Reason for cancellation"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={reason}
            onChange={handleReason}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCancelSubscription(false);
              setReason("");
            }}
          >
            Back
          </Button>
          <Button onClick={submitCancel} color="error">
            Cancel Subscription
          </Button>
        </DialogActions>
      </Dialog>

      <Paper sx={{ p: 3, textAlign: "left" }}>
        <Typography variant="body1" component="span">
          {subscriptionTier[currentSubscription]?.d}
        </Typography>
        {subscriptionTier[currentSubscription]?.c && (
          <Chip
            label={subscriptionTier[currentSubscription].c}
            sx={{
              ...getSubscriptionTierColor(currentSubscription),
              fontWeight: "bold",
              ml: 1,
            }}
          />
        )}
        <br />
        <br />

        {currentSubscription && currentSubscription !== '0' ? (
          <React.Fragment>
            <Typography variant="body1">
              Your current usage this month:
            </Typography>
            <ul>
              <li>
                <Typography>
                  {current.photos || 0} Photos{" "}
                  {photoLimit}
                </Typography>
              </li>
              <li>
                <Typography>
                  {current.emails || 0} emails sent{" "}
                  {emailLimit}
                </Typography>
              </li>
              <li>
                <Typography>
                  {storageGB} Gigabytes used out of{" "}
                  {storageGBLimit} GB
                </Typography>
              </li>
            </ul>
            <br />
          </React.Fragment>
        ) : (
          ""
        )}

        <Typography>{subscriptionDescription}</Typography>
        <br />
        <Button
          variant="contained"
          component={Link}
          to="/subscriptions"
          id="viewSubscriptions"
        >
          View Subscription Options
        </Button>

        {currentSubscription && currentSubscription !== '0' ? (
          <Box sx={{ mt: 5 }}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => {
                setCancelSubscription(true);
              }}
            >
              Cancel your subscription
            </Button>
          </Box>
        ) : (
          ""
        )}
      </Paper>
    </>
  );
};

export default SettingsSubscription;
